import Vue from "vue";

//set Headers payload
const authHeader = process.env.VUE_APP_REQUEST_HEADER;
const headers = {};
headers[authHeader] = process.env.VUE_APP_HUB_API_KEY;

const base = process.env.VUE_APP_HUB_API_HOST + "api/fence-accessories";

function errorMessage(error, fallback) {
  if (error && error.body && (error.body.error || error.body.message)) {
    return error.body.error || error.body.message;
  }
  return fallback;
}

/**
 * The fence accessories catalogue - automation kits, post boxes, wifi modules, spare remotes.
 *
 * The catalogue holds one row per country per product, so an offer picks the row for its own
 * country and gets that country's name and price. `byCountry` is keyed by the country as the offer
 * carries it, lowercased, and loaded on demand: an offer only ever needs its own.
 */
export default {
  namespaced: true,
  state: () => ({
    loadingAccessories: false,
    savingAccessories: false,
    accessoriesError: null,
    // every row, for the settings screen
    accessories: [],
    // { ro: [...], at: [...] } - what an offer picks from
    byCountry: {},
  }),
  getters: {
    /** The accessories on offer in one country, ready for a picker. */
    forCountry: (state) => (country) => {
      return state.byCountry[String(country || "").toLowerCase()] || [];
    },
  },
  mutations: {
    setLoadingAccessories(state, item) {
      state.loadingAccessories = item
    },
    setSavingAccessories(state, item) {
      state.savingAccessories = item
    },
    setAccessoriesError(state, item) {
      state.accessoriesError = item
    },
    loadAccessories(state, items) {
      state.accessories = items
    },
    loadAccessoriesForCountry(state, { country, items }) {
      state.byCountry = Object.assign({}, state.byCountry, {
        [String(country || "").toLowerCase()]: items
      })
    },
  },
  actions: {
    /** Every row, for the settings screen. */
    async fetchAccessories({ commit }) {
      commit("setLoadingAccessories", true);
      commit("setAccessoriesError", null);

      try {
        const response = await Vue.http.get(base, { headers: headers });
        commit("loadAccessories", response.body);
      } catch (error) {
        console.error("Error fetching fence accessories:", error);
        commit("setAccessoriesError", errorMessage(error, "Nu s-au putut incarca accesoriile."));
      } finally {
        commit("setLoadingAccessories", false);
      }
    },
    /** The active accessories for one country, for an offer's picker. */
    async fetchAccessoriesForCountry({ commit, state }, country) {
      const key = String(country || "").toLowerCase();

      if (!key || state.byCountry[key]) return;

      commit("setLoadingAccessories", true);
      commit("setAccessoriesError", null);

      try {
        const response = await Vue.http.get(base + "/" + encodeURIComponent(key), {
          headers: headers
        });
        commit("loadAccessoriesForCountry", { country: key, items: response.body });
      } catch (error) {
        console.error("Error fetching fence accessories for " + key + ":", error);
        commit("setAccessoriesError", errorMessage(error, "Nu s-au putut incarca accesoriile."));
      } finally {
        commit("setLoadingAccessories", false);
      }
    },
    // rows is a partial upsert keyed by code and country:
    // [{ code: "BFT_VIRGO", country: "RO", price: 850 }]
    async saveAccessories({ commit, dispatch }, rows) {
      commit("setSavingAccessories", true);

      try {
        await Vue.http.patch(base, { accessories: rows }, { headers: headers });
        await dispatch("fetchAccessories");
        M.toast({ html: "Accesorii salvate", classes: "bg-green-500" });
        return true;
      } catch (error) {
        console.error("Error saving fence accessories:", error);
        M.toast({
          html: errorMessage(error, "Accesoriile nu au putut fi salvate."),
          classes: "bg-red-500"
        });
        return false;
      } finally {
        commit("setSavingAccessories", false);
      }
    },
    /** Removes a product and every country version of it. */
    async deleteAccessory({ commit, dispatch }, code) {
      commit("setSavingAccessories", true);

      try {
        await Vue.http.delete(base + "/" + encodeURIComponent(code), { headers: headers });
        await dispatch("fetchAccessories");
        M.toast({ html: "Accesoriu sters", classes: "bg-green-500" });
        return true;
      } catch (error) {
        console.error("Error deleting fence accessory " + code + ":", error);
        M.toast({
          html: errorMessage(error, "Accesoriul nu a putut fi sters."),
          classes: "bg-red-500"
        });
        return false;
      } finally {
        commit("setSavingAccessories", false);
      }
    },
  }
}
